"use client"

import * as React from "react"
import { createPortal } from "react-dom"

import { cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useCreateOverlayForm } from "@/features/create-overlay/components/create-overlay-context"
import { CreateOverlayLayout } from "@/features/create-overlay/model/create-kind"

/**
 * The overlay's flat desktop fields, by slot, so every kind gets them without touching the shared components:
 * white, a thin border, no shadow, 40px pills (the height comes from `--control-h` on the dialog), the green ring
 * on focus; selects without the separator before the
 * chevron; labels 13px bold, 6px above their field.
 */
export const CREATE_OVERLAY_FLAT_FIELDS_CLASS = [
  "[&_[data-slot=label]]:!mb-0 [&_[data-slot=label]]:text-[13px] [&_[data-slot=label]]:font-bold [&_[data-slot=label]+*]:!mt-1.5",
  // Text inputs. The 16px text inset never replaces the left padding of a field with a leading icon (`pl-*`).
  "[&_[data-slot=input]]:border-border [&_[data-slot=input]]:bg-background [&_[data-slot=input]:not([class*=pr-])]:pr-4 [&_[data-slot=input]:not([class*=pl-])]:pl-4 [&_[data-slot=input]]:text-sm [&_[data-slot=input]]:shadow-none",
  "[&_[data-slot=input]:focus-visible]:border-primary [&_[data-slot=input]:focus-visible]:ring-primary/20",
  // Money (InputGroup with the currency prefix inside): the same flat 40px pill and green focus ring.
  "[&_[data-slot=input-group]]:border-border [&_[data-slot=input-group]]:bg-background [&_[data-slot=input-group]]:rounded-full [&_[data-slot=input-group]]:shadow-none",
  "[&_[data-slot=input-group]:focus-within]:!border-primary [&_[data-slot=input-group]:focus-within]:!ring-3 [&_[data-slot=input-group]:focus-within]:!ring-primary/20",
  // Phone (already one pill with the country inside, see PhoneInput): flat here too.
  "[&_.PhoneInput]:bg-background [&_.PhoneInput]:shadow-none",
  "[&_.PhoneInput:focus-within]:border-primary [&_.PhoneInput:focus-within]:ring-primary/20",
  // Selects (react-select, matched by its emotion labels: Tailwind cannot write the `__` of its class names):
  // flat, no separator, a small chevron inside the pill, the green ring on focus.
  "[&_[class*=-control]]:!shadow-none [&_[class*=indicatorSeparator]]:hidden",
  "[&_[class*=-control][class*=control--is-focused]]:!border-primary [&_[class*=-control][class*=control--is-focused]]:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary)_20%,transparent)]",
  // Date picker and other buttons used as fields.
  "[&_button[data-empty]]:bg-background [&_button[data-empty]]:px-4 [&_button[data-empty]]:shadow-none",
].join(" ")

/**
 * The body of a create form, laid out for where it is shown:
 * - desktop: the fields in the form column (the frame draws the header beside the preview panel);
 *   with layout "C", `preview` is drawn into the full-height panel on the right;
 * - phone: the fields alone (phone forms have no room for a preview).
 */
export function CreateOverlayBody({ children, preview }: { children: React.ReactNode; preview?: React.ReactNode }) {
  const { isMobile, layout, previewContainer } = useCreateOverlayForm()

  if (isMobile) return <div className='flex flex-col gap-5'>{children}</div>

  const showPreview = layout === CreateOverlayLayout.FormWithPreview && preview && previewContainer
  return (
    <>
      <div className={cn("flex min-w-0 flex-col gap-5 px-7 pt-1 pb-6", CREATE_OVERLAY_FLAT_FIELDS_CLASS)}>{children}</div>
      {showPreview ? createPortal(preview, previewContainer) : null}
    </>
  )
}

/** A titled group of fields: a small bold title, then the fields in a two-column grid. */
export function CreateOverlaySection({
  title,
  children,
  className,
}: {
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className='flex flex-col gap-3'>
      <h3 className='text-foreground text-[13.5px] font-extrabold'>{title}</h3>
      <div className={cn("grid gap-x-4 gap-y-3.5 sm:grid-cols-2", className)}>{children}</div>
    </section>
  )
}

/** A field label followed by the muted "(facultatif)" of the design. */
export function OptionalLabel({ children }: { children: React.ReactNode }) {
  const t = useTranslations("createOverlay")
  return (
    <>
      {children}
      <span className='text-muted-foreground ml-1 font-normal'>{t("optional")}</span>
    </>
  )
}

/** A segmented choice as a pill track (Homme / Femme), for a short fixed set on desktop. */
export function CreateOverlayChoice<TValue extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: TValue | undefined
  options: { value: TValue; label: string }[]
  onChange: (value: TValue) => void
  ariaLabel: string
}) {
  return (
    <div
      role='radiogroup'
      aria-label={ariaLabel}
      data-control=''
      className='bg-muted flex h-control items-center gap-1 p-[3px]'
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "h-full flex-1 rounded-full px-3 text-[13.5px] font-bold transition-colors focus-visible:ring-ring/50 focus-visible:outline-none focus-visible:ring-3",
              selected ? "bg-background text-foreground shadow-[0_1px_3px_rgba(16,24,40,0.12)]" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** A white card of the preview panel (one soft 16px radius). */
export function CreatePreviewCard({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("bg-card border-border/50 rounded-2xl border p-4", className)} {...props} />
}

/** Label / value rows inside a preview card. A missing value shows a muted dash. */
export function CreatePreviewFacts({ rows }: { rows: { label: string; value: React.ReactNode }[] }) {
  return (
    <dl className='divide-border/50 -my-1.5 divide-y text-sm'>
      {rows.map((row) => (
        <div key={row.label} className='flex items-center justify-between gap-3 py-2'>
          <dt className='text-muted-foreground shrink-0'>{row.label}</dt>
          <dd className='min-w-0 truncate text-right font-semibold'>{row.value || <span className='text-muted-foreground'>-</span>}</dd>
        </div>
      ))}
    </dl>
  )
}
